/**
 * DayEditModal Component (Refactored v2)
 * Modal for editing a calendar day with motivos AND work registration
 * 
 * Architecture:
 *   - Logic:    hooks/useDayEditModal.js     (state, effects, handlers)
 *   - UI:       sections/WorkRegistrationSection.jsx
 *               sections/TurnoChangeSection.jsx
 *               sections/MotivoSection.jsx
 *               sections/NotesSection.jsx
 */
import { X, Check, Trash2, Loader2, Sun, Moon, Zap } from 'lucide-react';
import useDayEditModal from './hooks/useDayEditModal';
import WorkRegistrationSection from './sections/WorkRegistrationSection';
import TurnoChangeSection from './sections/TurnoChangeSection';
import MotivoSection from './sections/MotivoSection';
import NotesSection from './sections/NotesSection';

const MONTHS = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

export default function DayEditModal({
    isOpen,
    onClose,
    dayData,
    conductorId,
    existingMarca,
    existingJornada,
    blockTurnoChange = false,
    tourStep = null,
    festivos = [],
    exchangeInfo,
    shiftOverride,
    onSave,
    onDelete,
    onNotesUpdated
}) {
    const state = useDayEditModal({
        isOpen, onClose, dayData, conductorId,
        existingMarca, existingJornada, festivos, tourStep,
        onSave, onDelete, onNotesUpdated
    });

    if (!isOpen || !dayData) return null;

    const date = new Date(dayData.dateStr);
    const dayOfWeek = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'][date.getDay()];

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-md bg-black/60">
            <div 
                className="relative rounded-[32px] shadow-2xl border w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col transition-colors duration-300"
                style={{ 
                    background: 'var(--bg-secondary)', 
                    borderColor: 'var(--border-color)',
                    color: 'var(--text-primary)'
                }}
            >

                {/* Header */}
                <div id="tour-edit-header" className="p-4 border-b border-white/10 flex items-center justify-between shrink-0">
                    <div>
                        <h2 className="text-lg font-black uppercase tracking-widest" style={{ color: 'var(--text-primary)' }}>
                            {dayData.day} de {MONTHS[date.getMonth()]}
                        </h2>
                        <p className="text-[10px] font-bold uppercase tracking-tighter" style={{ color: 'var(--text-muted)' }}>{dayOfWeek}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-[var(--text-muted)] hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-4 overflow-y-auto flex-1">
                    {state.loading ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {/* Current day info */}
                            <div className="p-4 rounded-2xl border" style={{ background: 'var(--bg-input)', borderColor: 'var(--border-color)' }}>
                                <p className="text-[10px] font-black uppercase tracking-widest mb-2" style={{ color: 'var(--text-muted)' }}>Cálculo original:</p>
                                <p className="font-bold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                                    {dayData.isVacacion && '🏖️ Vacaciones'}
                                    {dayData.isLibranza && !dayData.isVacacion && '🏠 Libranza'}
                                    {dayData.turno === 'M' && !dayData.isLibranza && !dayData.isVacacion && <><Sun className="w-4 h-4 text-amber-400" /> Turno Mañana</>}
                                    {dayData.turno === 'T' && !dayData.isLibranza && !dayData.isVacacion && <><Moon className="w-4 h-4 text-blue-400" /> Turno Tarde</>}
                                    {dayData.turno === 'N' && !dayData.isLibranza && !dayData.isVacacion && <><Moon className="w-4 h-4 text-indigo-400" /> Turno Noche</>}
                                    {dayData.turno === 'P' && !dayData.isLibranza && !dayData.isVacacion && <><Zap className="w-4 h-4 text-yellow-400" /> Turno Partido</>}
                                </p>
                            </div>

                            {/* Work Registration Section */}
                            {state.isWorkDay && !state.hasDayAusencia && (
                                <WorkRegistrationSection
                                    showWorkSection={state.showWorkSection}
                                    setShowWorkSection={state.setShowWorkSection}
                                    lineas={state.lineas}
                                    servidores={state.servidores}
                                    loadingServidores={state.loadingServidores}
                                    selectedLinea={state.selectedLinea}
                                    setSelectedLinea={state.setSelectedLinea}
                                    selectedServidor={state.selectedServidor}
                                    setSelectedServidor={state.setSelectedServidor}
                                    turnoJornada={state.turnoJornada}
                                    numeroBus={state.numeroBus}
                                    setNumeroBus={state.setNumeroBus}
                                    primasAplicables={state.primasAplicables}
                                />
                            )}

                            {/* Turno Change Section */}
                            {!state.hasDayAusencia && (
                                <TurnoChangeSection
                                    showTurnoSection={state.showTurnoSection}
                                    setShowTurnoSection={state.setShowTurnoSection}
                                    blockTurnoChange={blockTurnoChange}
                                    hasActiveTurnoChange={state.hasActiveTurnoChange}
                                    filteredTurnos={state.filteredTurnos}
                                    turnos={state.turnos}
                                    selectedTurno={state.selectedTurno}
                                    setSelectedTurno={state.setSelectedTurno}
                                    existingMarca={existingMarca}
                                    exchangeInfo={exchangeInfo}
                                    shiftOverride={shiftOverride}
                                    conductoresList={state.conductoresList}
                                    ctPeerInput={state.ctPeerInput}
                                    setCtPeerInput={state.setCtPeerInput}
                                    ctPeerId={state.ctPeerId}
                                    setCtPeerId={state.setCtPeerId}
                                    ctSearchResults={state.ctSearchResults}
                                    setCtSearchResults={state.setCtSearchResults}
                                    showCtAutocomplete={state.showCtAutocomplete}
                                    setShowCtAutocomplete={state.setShowCtAutocomplete}
                                    cfMode={state.cfMode}
                                    setCfMode={state.setCfMode}
                                    deudores={state.deudores}
                                    loadingDeudores={state.loadingDeudores}
                                    cfCompaneroId={state.cfCompaneroId}
                                    setCfCompaneroId={state.setCfCompaneroId}
                                    cfCompaneroNombre={state.cfCompaneroNombre}
                                    setCfCompaneroNombre={state.setCfCompaneroNombre}
                                    handleDeleteTurnoOnly={state.handleDeleteTurnoOnly}
                                    deleting={state.deleting}
                                />
                            )}

                            {/* Motivo Section */}
                            <MotivoSection
                                showMotivoSection={state.showMotivoSection}
                                setShowMotivoSection={state.setShowMotivoSection}
                                motivos={state.motivos}
                                selectedMotivo={state.selectedMotivo}
                                setSelectedMotivo={state.setSelectedMotivo}
                                extendEnabled={state.extendEnabled}
                                setExtendEnabled={state.setExtendEnabled}
                                extendEndDate={state.extendEndDate}
                                setExtendEndDate={state.setExtendEndDate}
                                dayData={dayData}
                                hsSaldo={state.hsSaldo}
                            />

                            {/* Notes Section */}
                            <NotesSection
                                showComentarioSection={state.showComentarioSection}
                                setShowComentarioSection={state.setShowComentarioSection}
                                notas={state.notas}
                                loadingNotas={state.loadingNotas}
                                comentario={state.comentario}
                                setComentario={state.setComentario}
                                noteText={state.noteText}
                                setNoteText={state.setNoteText}
                                selectedTipo={state.selectedTipo}
                                setSelectedTipo={state.setSelectedTipo}
                                editingNotaId={state.editingNotaId}
                                setEditingNotaId={state.setEditingNotaId}
                                noteReminderEnabled={state.noteReminderEnabled}
                                setNoteReminderEnabled={state.setNoteReminderEnabled}
                                noteReminderHora={state.noteReminderHora}
                                setNoteReminderHora={state.setNoteReminderHora}
                                reminderEnabled={state.reminderEnabled}
                                setReminderEnabled={state.setReminderEnabled}
                                reminderHora={state.reminderHora}
                                setReminderHora={state.setReminderHora}
                                reminderNotificado={state.reminderNotificado}
                                setReminderNotificado={state.setReminderNotificado}
                                handleEditNota={state.handleEditNota}
                                handleDeleteNota={state.handleDeleteNota}
                            />
                        </div>
                    )}
                </div>

                {/* Actions */}
                <div className="p-4 border-t border-white/10 shrink-0">
                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            className="flex-1 px-4 py-4 rounded-2xl font-black uppercase tracking-widest text-xs transition-all border"
                            style={{ background: 'var(--bg-tertiary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={state.handleSave}
                            disabled={!state.canSave}
                            className={`flex-1 px-4 py-4 rounded-2xl font-black uppercase tracking-widest text-xs transition-all shadow-lg flex items-center justify-center gap-2 transition-all active:scale-95 ${
                                !state.canSave 
                                    ? 'bg-gray-600 opacity-50 cursor-not-allowed' 
                                    : 'bg-gradient-to-r from-orange-500 to-amber-500 shadow-orange-500/20 text-white'
                            }`}
                        >
                            {state.saving ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    <span>Guardando</span>
                                </>
                            ) : (
                                <>
                                    <Check className="w-5 h-5" />
                                    <span>Guardar</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
